import { Injectable } from "@angular/core";
import { Observable, Subject } from "rxjs";
import { NGXLogger } from "ngx-logger";

import { AddMailsystemEvent } from "@core/events/add-mail-system.event";
import { EditMailsystemEvent } from "@core/events/edit-mail-system.event";
import { AddUserEvent } from "@core/events/add-user.event";
import { EditUserEvent } from "@core/events/edit-user.event";
import { AddTaskEvent } from "@core/events/add-task.event";
import { TestConnectionResultEvent } from "@core/events/test-connection-result-event";
import { TaskAssignedEvent } from "@core/events/task-assigned.event";
import { MailsystemFailureEvent } from "@core/events/mailsystem-failure.event";
import { EditMailsystemActiveEvent } from "@core/events/edit-mail-system-active.event";
import { UserConnectionEvent } from "@core/events/user-connection.event";
import { TaskCompletedEvent } from "@core/events/task-completed.event";
import { TaskUnassignedEvent } from "@core/events/task-unassigned.event";
import { TaskExtendedEvent } from "@core/events/task-extended.event";
import { AddTemplateEvent } from "@core/events/add-template.event";
import { EditTemplateEvent } from "@core/events/edit-template.event";
import { DeleteTemplateEvent } from "@core/events/delete-template.event";

@Injectable()
export abstract class EventService {

	abstract stop();
	abstract createHub();

	public isAuthenticated: boolean;

	public addMailSystemSubject = new Subject<AddMailsystemEvent>();
	public editMailSystemSubject = new Subject<EditMailsystemEvent>();
	public editMailSystemActiveSubject = new Subject<EditMailsystemActiveEvent>();
	public addTaskSubject = new Subject<AddTaskEvent>();
	public addUserSubject = new Subject<AddUserEvent>();
	public editUserSubject = new Subject<EditUserEvent>();
	public testConnectionResultSubject = new Subject<TestConnectionResultEvent>();
	public taskAssignedSubject = new Subject<TaskAssignedEvent>();
	public taskUnassignedSubject = new Subject<TaskUnassignedEvent>();
	public taskCompletedSubject = new Subject<TaskCompletedEvent>();
	public taskExtendedSubject = new Subject<TaskExtendedEvent>();
	public mailsystemFailureSubject = new Subject<MailsystemFailureEvent>();
	public userConnectionSubject = new Subject<UserConnectionEvent>();
	public addTemplateSubject = new Subject<AddTemplateEvent>();
	public editTemplateSubject = new Subject<EditTemplateEvent>();
	public deleteTemplateSubject = new Subject<DeleteTemplateEvent>();

	constructor(public logger: NGXLogger) { }

	public async start() {

		this.logger.info(`[EventService]: Initializing`);
		this.isAuthenticated = true;
		this.logger.info(`[EventService]: Checking authentication state: `, this.isAuthenticated);

		if (this.isAuthenticated) {

			this.handleAuthenticated();
		}
	}

	private handleAuthenticated() {

		this.createHub();
	}

	public onAddMailSystem(): Observable<AddMailsystemEvent> {

		return this.addMailSystemSubject.asObservable();
	}

	public onEditMailSystem(): Observable<EditMailsystemEvent> {

		return this.editMailSystemSubject.asObservable();
	}

	public onEditMailSystemActive(): Observable<EditMailsystemActiveEvent> {

		return this.editMailSystemActiveSubject.asObservable();
	}

	public onAddUser(): Observable<AddUserEvent> {

		return this.addUserSubject.asObservable();
	}

	public onEditUser(): Observable<EditUserEvent> {

		return this.editUserSubject.asObservable();
	}

	public onAddTask(): Observable<AddTaskEvent> {

		return this.addTaskSubject.asObservable();
	}

	public onTestConnectionResult(): Observable<TestConnectionResultEvent> {

		return this.testConnectionResultSubject.asObservable();
	}

	public onTaskAssigned(): Observable<TaskAssignedEvent> {

		return this.taskAssignedSubject.asObservable();
	}

	public onTaskUnassigned(): Observable<TaskUnassignedEvent> {

		return this.taskUnassignedSubject.asObservable();
	}

	public onTaskCompleted(): Observable<TaskCompletedEvent> {

		return this.taskCompletedSubject.asObservable();
	}

	public onTaskExtended(): Observable<TaskExtendedEvent> {

		return this.taskExtendedSubject.asObservable();
	}

	public onMailsystemFailure(): Observable<MailsystemFailureEvent> {

		return this.mailsystemFailureSubject.asObservable();
	}

	public onAddTemplate(): Observable<AddTemplateEvent> {

		return this.addTemplateSubject.asObservable();
	}

	public onEditTemplate(): Observable<EditTemplateEvent> {

		return this.editTemplateSubject.asObservable();
	}

	public onDeleteTemplate(): Observable<DeleteTemplateEvent> {

		return this.deleteTemplateSubject.asObservable();
	}

	public onUserConnection(): Observable<UserConnectionEvent> {

		return this.userConnectionSubject.asObservable();
	}

	public connectedSubject = new Subject<any>();

	public getConnected(): Observable<any> {

		return this.connectedSubject.asObservable();
	}
}